import { useState } from 'react';

import { ClassNameMap } from '@material-ui/core/styles/withStyles';
import {
  DevHubGrid,
  DevHubIcon,
  devhubMakeStyles,
  DevHubModal,
  DevHubTypography,
} from '@devxdiscover/devhub-ui';

import GreenScoreIcon from './GreenScoreIcon';
import GreenScoreModalContainer from './GreenScoreModal/GreenScoreModalContainer';
import MetricsModalHeading from './GreenScoreModal/MetricsModalHeading';
import ExplanationModalHeading from './GreenScoreModal/ExplanationModalHeading';
import GreenScoreComputationModal from './GreenScoreModal/GreenScoreComputationModal';
import GreenScoreExplanationModal from './GreenScoreModal/GreenScoreExplanationModal';
import { InfoCard } from '@backstage/core-components';

// Component Props for GreenScoreCard
interface GreenScoreCardProps {
  // The score will always exist as a key, but may be null
  score: number | null;
  // If there's no score, metrics will not exist as a key
  metrics: GreenScoreProfileMetricProps[];
}

/**
 * Contains Green Energy Score and 2 handlers that trigger a modal
 * - Computational Information modal (Static)
 * - Official Metrics that determine the Green Score (Dynamic)
 * @param props GreenScoreCardProps -- ApplicationId used to fetch Green Energy Profile
 * @returns Component created to show new Green Energy Score
 */
function GreenScoreCard(props: GreenScoreCardProps) {
  const [isCalculationModalOpen, setIsCalculationModalOpen] =
    useState<boolean>(false);
  const [isExplanationModalOpen, setIsExplanationModalOpen] =
    useState<boolean>(false);

  const classes: ClassNameMap<string> = devhubMakeStyles({
    icon: {
      height: 'auto',
      width: '50px',
    },
    card: {
      height: '80px',
      display: 'flex',
      alignItems: 'center',

      backgroundColor: 'white',
      padding: '20px',
    },
    linkContainer: {
      display: 'flex',
      height: '100%',
    },
    link: {
      paddingBottom: '5px',
      '&:focus': {
        textDecoration: 'underline',
      },
      '&:hover': {
        textDecoration: 'underline',
        cursor: 'pointer',
      },
    },
    scoreContainer: {
      display: 'flex',
      alignItems: 'center',
      '&:hover': {
        cursor: 'pointer',
      },
    },
    score: {
      color: '#3ea436',
    },
  })();

  // Do not show Green Score Profile Card if application has no score
  if (!props.score) {
    return null;
  }

  return (
    <InfoCard cardClassName={classes.card}>
      <DevHubGrid
        className={classes.scoreContainer}
        onClick={() => setIsCalculationModalOpen(true)}
      >
        <DevHubGrid direction="row" justifyContent="flex-start" centerContent>
          <DevHubIcon className={classes.icon}>
            <GreenScoreIcon />
          </DevHubIcon>
        </DevHubGrid>
        <DevHubGrid direction="column" lrMargin="small">
          <DevHubTypography fontSize="small">Green Score</DevHubTypography>
          <DevHubTypography fontSize="large" className={classes.score}>
            {props.score}
          </DevHubTypography>
        </DevHubGrid>
      </DevHubGrid>
      <DevHubGrid
        direction="row"
        lrMargin="small"
        alignItems="flex-end"
        className={classes.linkContainer}
      >
        <DevHubTypography
          fontSize="small"
          fontColor="blue"
          className={classes.link}
          onClick={() => setIsExplanationModalOpen(true)}
        >
          What is this Green Score and how is it calculated?
        </DevHubTypography>
        {/* Modals to render. Rendering them down here keeps their event bubbling clean */}
        <DevHubModal
          open={isCalculationModalOpen}
          onClose={() => setIsCalculationModalOpen(false)}
        >
          <GreenScoreModalContainer
            heading={<MetricsModalHeading score={props.score} />}
          >
            <GreenScoreComputationModal metrics={props?.metrics} />
          </GreenScoreModalContainer>
        </DevHubModal>
        <DevHubModal
          open={isExplanationModalOpen}
          onClose={() => setIsExplanationModalOpen(false)}
        >
          <GreenScoreModalContainer heading={<ExplanationModalHeading />}>
            <GreenScoreExplanationModal />
          </GreenScoreModalContainer>
        </DevHubModal>
      </DevHubGrid>
    </InfoCard>
  );
}

export default GreenScoreCard;
